import type { UploadEvent } from '../sensors';

/**
 * Offline queue for detected events.
 *
 * Survives tab refresh + offline drives. PWA implementation uses IndexedDB
 * (universal); a future Capacitor implementation will swap in
 * `@capacitor-community/sqlite` behind the same interface.
 *
 * Drain semantics: `drain(max)` returns up to `max` events AND removes them
 * atomically. If the upload fails, callers re-enqueue. Simpler than a
 * "tombstone" flag at the cost of one IDB transaction per upload batch —
 * acceptable for our volumes.
 */
export interface EventQueue {
  enqueue: (e: UploadEvent) => Promise<void>;
  size: () => Promise<number>;
  drain: (max: number) => Promise<UploadEvent[]>;
  /** Re-add events whose upload failed. */
  requeue: (events: UploadEvent[]) => Promise<void>;
  clear: () => Promise<void>;
}

const DB_NAME = 'joltstream';
const DB_VERSION = 1;
const STORE = 'events';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

class IndexedDbQueue implements EventQueue {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = openDb();
    return this.dbPromise;
  }

  async enqueue(e: UploadEvent): Promise<void> {
    const db = await this.db();
    await tx(db, 'readwrite', (store) => store.add(e));
  }

  async size(): Promise<number> {
    const db = await this.db();
    return await tx(db, 'readonly', (store) => store.count());
  }

  async drain(max: number): Promise<UploadEvent[]> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite');
      const store = t.objectStore(STORE);
      const taken: UploadEvent[] = [];
      const keysToDelete: IDBValidKey[] = [];
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cur = cursorReq.result;
        if (cur && taken.length < max) {
          const { id: _id, ...event } = cur.value as UploadEvent & { id: number };
          taken.push(event as UploadEvent);
          keysToDelete.push(cur.primaryKey);
          cur.continue();
        } else {
          keysToDelete.forEach((k) => store.delete(k));
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
      t.oncomplete = () => resolve(taken);
      t.onerror = () => reject(t.error);
    });
  }

  async requeue(events: UploadEvent[]): Promise<void> {
    const db = await this.db();
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite');
      const store = t.objectStore(STORE);
      events.forEach((e) => store.add(e));
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  }

  async clear(): Promise<void> {
    const db = await this.db();
    await tx(db, 'readwrite', (store) => store.clear());
  }
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = op(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function createEventQueue(): EventQueue {
  return new IndexedDbQueue();
}
